import * as React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import {ReleaseLevel} from '../../../@types/component';

/**
 * The release-level and release-notes prompt shown before a draft Component is published. Shared
 * by every entry point that can trigger a publish -- the explorer's row action menu and the
 * workspace's top resource menu -- so a caller only needs to supply the component being published
 * and what to do once the user confirms.
 */
export default function PublishComponent(props: {
    open: boolean;
    componentName: string;
    publishing?: boolean;
    onClose: () => void;
    onConfirm: (releaseLevel: ReleaseLevel, releaseNotes: string) => void;
}): React.ReactElement {
    const { open, componentName, publishing, onClose, onConfirm } = props;

    const [releaseLevel, setReleaseLevel] = React.useState<ReleaseLevel>(ReleaseLevel.PATCH);
    const [releaseNotes, setReleaseNotes] = React.useState<string>('');

    React.useEffect(() => {
        if (open) {
            setReleaseLevel(ReleaseLevel.PATCH);
            setReleaseNotes('');
        }
    }, [open]);

    const notesValid = releaseNotes.trim() !== '';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Publish {componentName || 'this component'}?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Publishing creates a new, immutable version from this draft, readable by every
                    group and available for other models to pin. The draft itself is unaffected and
                    stays open for further editing -- none of its mapping or test runs carry over to
                    the published copy. This cannot be undone.
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="publish-release-level-label">Release level</InputLabel>
                    <Select
                        labelId="publish-release-level-label"
                        label="Release level"
                        value={releaseLevel}
                        onChange={(e: SelectChangeEvent): void => setReleaseLevel(e.target.value as ReleaseLevel)}
                    >
                        {Object.values(ReleaseLevel).map(level => (
                            <MenuItem key={level} value={level}>
                                {level.charAt(0) + level.slice(1).toLowerCase()}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Release notes"
                    aria-label="release notes"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={releaseNotes}
                    onChange={(e): void => setReleaseNotes(e.target.value)}
                    multiline
                    minRows={3}
                    required
                    error={!notesValid}
                    helperText="What changed since the previous release -- required to publish"
                />
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={!notesValid || publishing}
                    onClick={(): void => onConfirm(releaseLevel, releaseNotes.trim())}
                >
                    Publish
                </Button>
            </DialogActions>
        </Dialog>
    );
}
